import styled from 'styled-components';
import { Switch, Route, Redirect, RouteComponentProps, matchPath } from 'react-router-dom';
import { FileDocumentType } from '@nkr/enums/document-type';
import { Button } from 'react-bootstrap';
import { downloadFile } from '@nkr/services/download-file';
import { ActionCenterTab } from './action-center/tabs';
import { getDefaultSettingsTab } from './settings/tabs';
import LandingNav, { OpoNavKey } from './landing-nav';
import useOpoFiles from '@nkr/store/opo/hooks/use-opo-files';
import usePortalOpo from '@nkr/store/opo/hooks/use-portal-opo';
import ActionCenterContent from './action-center';
import SettingsContent from './settings';

const LandingLayout = styled.div`
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    gap: 20px;
    min-height: calc(100vh - 170px);
`;

const MainContent = styled.main`
    min-width: 0;
`;

const AdminViewBanner = styled.div`
    margin-bottom: 12px;
    padding: 8px 14px;
    border: 1px solid #CBD9E8;
    border-radius: 8px;
    background: #EEF3F9;
    color: #16365A;
    font-size: 13px;
`;

const LandingPage = ({ match, location, history }: RouteComponentProps<{}>) => {
    const { opoId, opoName, isAdminView, isAdmin } = usePortalOpo();
    const { list, download } = useOpoFiles(opoId);

    const basePath = match.path.replace(/\/+$/, '');
    const actionCenterPath = `${basePath}/${OpoNavKey.ActionCenter}`;
    const settingsPath = `${basePath}/${OpoNavKey.Settings}`;
    const activeNav = matchPath<{ section: string }>(location.pathname, { path: `${basePath}/:section` })?.params.section;

    const handleFileDownload = (type: FileDocumentType, fileName: string) => {
        download(opoId, type).then(response => {
            downloadFile(response.data, fileName, 'application/pdf');
        });
    }

    const hasAgreementFile = list.find(x => x.documentTypeId == FileDocumentType.OpoContract)?.files.length > 0;
    const hasAddendumFile = list.find(x => x.documentTypeId == FileDocumentType.OpoContractAddendum)?.files.length > 0;

    return (
        <>
            {isAdminView && (
                <AdminViewBanner>Viewing <strong>{opoName}</strong> as an NKR administrator</AdminViewBanner>
            )}

            <LandingLayout>
                <LandingNav title={opoName} activeKey={activeNav} onSelect={key => history.push(`${basePath}/${key}`)} />
                <MainContent>
                    <Switch>
                        <Route
                            path={`${actionCenterPath}/:tab`}
                            render={({ match: { params } }) =>
                                <ActionCenterContent
                                    activeTab={params.tab}
                                    onTabSelect={tab => history.push(`${actionCenterPath}/${tab}`)}
                                    onSettingsSelect={tab => history.push(`${settingsPath}/${tab}`)}
                                />
                            }
                        />
                        <Redirect exact from={actionCenterPath} to={`${actionCenterPath}/${ActionCenterTab.ToDo}`} />
                        <Route path={`${basePath}/${OpoNavKey.Billing}`} render={() => null} />
                        <Route path={`${basePath}/${OpoNavKey.ContractsDocuments}`} render={() => null} />
                        <Route
                            path={`${settingsPath}/:tab`}
                            render={({ match: { params } }) =>
                                <SettingsContent activeTab={params.tab} onTabSelect={tab => history.push(`${settingsPath}/${tab}`)} />
                            }
                        />
                        <Redirect exact from={settingsPath} to={`${settingsPath}/${getDefaultSettingsTab(isAdmin)}`} />
                        <Redirect to={actionCenterPath} />
                    </Switch>
                </MainContent>
            </LandingLayout>

            <div style={{ position: 'relative', paddingTop: 10, width: '98%', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    2019-{new Date().getFullYear()} Best Match Corporation dba <a href='https://www.kidneyregistry.com' target='_blank'>National Kidney Registry</a>. All rights reserved.
                </div>
                <div>
                    <a className='btn btn-link' href='https://www.bestmatchcorporation.com/terms/' target='_blank'>Terms &amp; Conditions</a>
                    {hasAgreementFile && (
                        <>
                            &nbsp;|&nbsp;<Button bsStyle='link' onClick={() => handleFileDownload(FileDocumentType.OpoContract, `contract-${opoId}.pdf`)}>Service Provider Agreement</Button>
                        </>
                    )}

                    {hasAddendumFile && (
                        <>
                            &nbsp;|&nbsp;<Button bsStyle='link' onClick={() => handleFileDownload(FileDocumentType.OpoContractAddendum, `contract-${opoId}.pdf`)}>Service Provider Agreement Addendum</Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default LandingPage;